import React, { useState, useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import { Icon } from "../../../../../elements";
import { API } from "aws-amplify";
import {
    getVaultPermissions,
  putVaultPermissions,
} from "../../../../../graphql/queries";
import SuccessShareVaultModal from "../SuccessShareVaultModal/SuccessShareVaultModal";
import { useStore } from "../../../../../hooks";
import {
  StyledDialog,
  DialogHeader,
  HeaderTop,
  DialogTitle,
  DialogDescription,
  CloseButton,
  DialogContent,
  SearchContainer,
  SearchInput,
  SearchIconWrapper,
  UserListContainer,
  UserItem,
  UserLeftSection,
  UserInfo,
  UserName,
  UserActions,
  StyledSelect,
  DeleteButton,
  DialogFooter,
  CancelButton,
  ShareButton,
  SearchResults,
  SearchResultItem,
  NoUsersMessage,
  SharedUsersLabel,
  SharedUsersCount,
  SharedUsersSection,
  CurrentlySharedUsers,
  AddedUsersLabel,
} from "./ShareVaultModal.styled";

const PERMISSION_OPTIONS = ["VIEW", "UPDATE", "DELETE", "SHARE"];

const ShareVaultModal = ({ isOpen, onClose, vaultId, vaultName }) => {
  const [searchValue, setSearchValue] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const { authStore } = useStore();
  const [newUsers, setNewUsers] = useState([]);
  const [, setExistingPermissions] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(false);
  const [existingUsers, setExistingUsers] = useState([]);
  const [deletedUsers] = useState([]);
  const [,setIsDeletingUsers] = useState({});

  const fetchVaultPermissions = async (folderId) => {
    try {
      setIsLoadingPermissions(true);
      const response = await API.graphql({
        query: getVaultPermissions,
        variables: {
          input: {
            customer_id: authStore.selectedAccount.id,
            folder_id: folderId,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.getVaultPermissions.statusCode === "200") {
        const result = JSON.parse(response.data.getVaultPermissions.body);
        return result;
      } else {
        throw new Error("Error fetching vault permissions");
      }
    } catch (error) {
      console.error("Error fetching vault permissions:", error);
      return null;
    } finally {
      setIsLoadingPermissions(false);
    }
  };

  const updateVaultPermissions = async (folderId, shareWith, isDeleting = false) => {
    try {
      if (!isDeleting) {
        setIsSharing(true);
      }
      const response = await API.graphql({
        query: putVaultPermissions,
        variables: {
          input: {
            customer_id: authStore.selectedAccount.id,
            folder_id: folderId,
            share_with: shareWith,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.putVaultPermissions.statusCode === "200") {
        return true;
      } else {
        throw new Error("Error setting vault permissions");
      }
    } catch (error) {
      console.error("Error setting vault permissions:", error);
      return false;
    } finally {
      if (!isDeleting) {
        setIsSharing(false);
      }
    }
  };

  useEffect(() => {
    const loadPermissions = async () => {
      if (isOpen && vaultId) {
        try {
          const permissions = await fetchVaultPermissions(vaultId);
          if (permissions) {
            setExistingPermissions(permissions);

            const uniqueUsersMap = new Map();

            permissions.sharedWith?.forEach((share) => {
              const user = authStore.users.find((u) => u.id === share.user_id);
              if (!user) return;

              if (uniqueUsersMap.has(share.user_id)) {
                const existingUser = uniqueUsersMap.get(share.user_id);
                const newPermission = share.policy && share.policy.length > 0
                  ? share.policy[share.policy.length - 1]
                  : "VIEW";
                
                if (existingUser.selectedPermission !== newPermission) {
                  uniqueUsersMap.set(share.user_id, {
                    ...existingUser,
                    selectedPermission: newPermission
                  });
                }
              } else {
                uniqueUsersMap.set(share.user_id, {
                  id: share.user_id,
                  name: `${user.first_name} ${user.last_name}`,
                  selectedPermission: share.policy && share.policy.length > 0
                    ? share.policy[share.policy.length - 1]
                    : "VIEW"
                });
              }
            });

            const currentUsers = Array.from(uniqueUsersMap.values());
            
            setExistingUsers(currentUsers);
            setNewUsers([]);
          }
        } catch (error) {
          console.error("Error loading permissions:", error);
        }
      }
    };

    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, vaultId, authStore.users, authStore.customer]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (value.trim()) {
      const results = authStore.users.filter(
        (user) =>
          `${user.first_name} ${user.last_name}`
            .toLowerCase()
            .includes(value.toLowerCase()) &&
          user.id !== authStore.userData.id &&
          !newUsers.some((selectedUser) => selectedUser.id === user.id) &&
          !existingUsers.some((existingUser) => existingUser.id === user.id)
      );
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectUser = (user) => {
    if (!newUsers.find((u) => u.id === user.id)) {
      setNewUsers([
        ...newUsers,
        {
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          selectedPermission: "VIEW",
        },
      ]);
    }
    setSearchValue("");
    setSearchResults([]);
  };

  const handlePermissionChange = (
    userId,
    newPermission,
    isExisting = false
  ) => {
    if (isExisting) {
      setExistingUsers(
        existingUsers.map((user) =>
          user.id === userId
            ? { ...user, selectedPermission: newPermission }
            : user
        )
      );
    } else {
      setNewUsers(
        newUsers.map((user) =>
          user.id === userId
            ? { ...user, selectedPermission: newPermission }
            : user
        )
      );
    }
  };

  const handleDeleteUser = async (userId, isExisting = false) => {
    if (isExisting) {
      const userToDelete = existingUsers.find(user => user.id === userId);
      if (userToDelete) {
        // Remove user immediately from UI
        setExistingUsers(existingUsers.filter(user => user.id !== userId));
        
        // Make API call in background
        setIsDeletingUsers(prev => ({ ...prev, [userId]: true }));
        try {
          const formattedUser = [{
            user_id: userToDelete.id,
            roles: {
              VIEW: userToDelete.selectedPermission === "VIEW",
              UPDATE: userToDelete.selectedPermission === "UPDATE",
              DELETE: userToDelete.selectedPermission === "DELETE",
              SHARE: userToDelete.selectedPermission === "SHARE",
            },
            active: false
          }];

          await updateVaultPermissions(vaultId, formattedUser, true);
        } catch (error) {
          console.error("Error removing user access:", error);
        } finally {
          setIsDeletingUsers(prev => {
            const newState = { ...prev };
            delete newState[userId];
            return newState;
          });
        }
      }
    } else {
      setNewUsers(newUsers.filter((user) => user.id !== userId));
    }
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      const uniqueUsers = [...existingUsers, ...newUsers, ...deletedUsers].reduce((acc, current) => {
        const x = acc.find(item => item.id === current.id);
        if (!x) {
          return acc.concat([current]);
        } else {
          return acc.map(item => 
            item.id === current.id ? current : item
          );
        }
      }, []);

      const formattedUsers = uniqueUsers.map((user) => ({
        user_id: user.id,
        roles: {
          VIEW: user.selectedPermission === "VIEW",
          UPDATE: user.selectedPermission === "UPDATE",
          DELETE: user.selectedPermission === "DELETE",
          SHARE: user.selectedPermission === "SHARE",
        },
        active: user.active === false ? false : undefined
      }));

      const success = await updateVaultPermissions(vaultId, formattedUsers);
      if (success) {
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error sharing vault:", error);
    } finally {
      setIsSharing(false);
    }
  };

  const handleCloseAll = () => {
    setShowSuccessModal(false);
    onClose();
  };

  const handleAddMore = () => {
    // Move newly added users to existing users
    setExistingUsers([...existingUsers, ...newUsers]);
    // Clear the new users array
    setNewUsers([]);
    setShowSuccessModal(false);
  };

  return (
    <>
      <StyledDialog
        open={isOpen && !showSuccessModal}
        onClose={onClose}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              maxWidth: "100%",
              margin: "16px",
            },
          },
        }}
      >
        <DialogHeader>
          <HeaderTop>
            <DialogTitle>Share {vaultName}</DialogTitle>
            <CloseButton
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <Icon
                name="closeIconGray"
                width="20px"
                height="20px"
                color="#F2F2F2"
              />
            </CloseButton>
          </HeaderTop>
          <DialogDescription>
            Search and select the users to add to this Vault.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          {isLoadingPermissions ? (
            <div>Loading permissions...</div>
          ) : (
            <>
              <SearchContainer>
                <SearchIconWrapper>
                  <Icon name="magnifier" height="16px" width="16px" />
                </SearchIconWrapper>
                <SearchInput
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search Users"
                />
                {searchResults.length > 0 && (
                  <SearchResults>
                    {searchResults.map((user) => (
                      <SearchResultItem
                        key={user.id}
                        onClick={() => handleSelectUser(user)}
                      >
                        {`${user.first_name} ${user.last_name}`}
                      </SearchResultItem>
                    ))}
                  </SearchResults>
                )}
              </SearchContainer>
              {newUsers.length > 0 && (
                <AddedUsersLabel>
                  Added {newUsers.length} users
                </AddedUsersLabel>
              )}
              <UserListContainer>
                {newUsers.length === 0 ? (
                  <NoUsersMessage>No users selected</NoUsersMessage>
                ) : (
                  newUsers.map((user) => (
                    <UserItem key={user.id}>
                      <UserLeftSection>
                        <UserInfo>
                          <UserName>{user.name}</UserName>
                        </UserInfo>
                      </UserLeftSection>
                      <UserActions>
                        <StyledSelect
                          value={user.selectedPermission}
                          onChange={(e) =>
                            handlePermissionChange(user.id, e.target.value)
                          }
                          size="small"
                        >
                          {PERMISSION_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                            </MenuItem>
                          ))}
                        </StyledSelect>
                        <DeleteButton
                          onClick={() => handleDeleteUser(user.id)}
                          aria-label="delete user"
                        >
                          <Icon
                            name="closeIconGray"
                            width="16px"
                            height="16px"
                            color="#F2F2F2"
                          />
                        </DeleteButton>
                      </UserActions>
                    </UserItem>
                  ))
                )}
              </UserListContainer>
              <div>
                <SharedUsersLabel>Currently shared with</SharedUsersLabel>
                <SharedUsersCount>
                  Shared with {existingUsers.length} users
                </SharedUsersCount>
              </div>
              {existingUsers.length > 0 && (
                <SharedUsersSection>
                  <CurrentlySharedUsers>
                    {existingUsers.map((user) => (
                      <UserItem key={user.id}>
                        <UserLeftSection>
                          <UserInfo>
                            <UserName>{user.name}</UserName>
                          </UserInfo>
                        </UserLeftSection>
                        <UserActions>
                          <StyledSelect
                            value={user.selectedPermission}
                            onChange={(e) =>
                              handlePermissionChange(
                                user.id,
                                e.target.value,
                                true
                              )
                            }
                            size="small"
                          >
                            {PERMISSION_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()}
                              </MenuItem>
                            ))}
                          </StyledSelect>
                          <DeleteButton
                            onClick={() => handleDeleteUser(user.id, true)}
                            aria-label="delete user"
                          >
                            <Icon
                              name="closeIconGray"
                              width="16px"
                              height="16px"
                              color="#F2F2F2"
                            />
                          </DeleteButton>
                        </UserActions>
                      </UserItem>
                    ))}
                  </CurrentlySharedUsers>
                </SharedUsersSection>
              )}
            </>
          )}
        </DialogContent>
        <DialogFooter>
          <CancelButton
            onClick={handleCloseAll}
            disabled={isSharing}
          >
            Cancel
          </CancelButton>
          <ShareButton
            onClick={handleShare}
            disabled={
              newUsers.length === 0 ||
              isSharing
            }
          >
            {isSharing
              ? "Sharing..."
              : `Share with ${newUsers.length} users`}
          </ShareButton>
        </DialogFooter>
      </StyledDialog>

      {showSuccessModal && (
        <SuccessShareVaultModal
          isOpen={showSuccessModal}
          onClose={handleCloseAll}
          usersCount={newUsers.length}
          vaultName={vaultName}
          onAddMore={handleAddMore}
          onGoToVault={handleCloseAll}
          users={newUsers}
          onReload={async () => {
            const permissions = await fetchVaultPermissions(vaultId);
            if (permissions) {
              setExistingPermissions(permissions);
            }
          }}
        />
      )}
    </>
  );
};

export default ShareVaultModal;
